import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../styles/AuthModal.css';

export default function AuthModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { login, signup, loginWithGoogle, resetPassword, resendVerificationEmail, error } = useAuth();
  const navigate = useNavigate();

  const validatePassword = () => {
    // Minimum 8 characters
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return false;
    }

    // At least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      setValidationError('Password must contain at least one uppercase letter');
      return false;
    }

    // At least one lowercase letter
    if (!/[a-z]/.test(password)) {
      setValidationError('Password must contain at least one lowercase letter');
      return false;
    }

    // At least one number
    if (!/\d/.test(password)) {
      setValidationError('Password must contain at least one number');
      return false;
    }

    // No spaces allowed
    if (/\s/.test(password)) {
      setValidationError('Password cannot contain spaces');
      return false;
    }

    // Check if passwords match during signup
    if (!isLogin && password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }

    setValidationError('');
    return true;
  };

  // Add password requirements helper text
  const getPasswordRequirements = () => {
    if (!isLogin && !isResetPassword) {
      return (
        <div className="password-requirements">
          • At least one uppercase letter<br />
          • At least one lowercase letter<br />
          • At least one numeral (0-9)<br />
          • Minimum 8 characters<br />
          • No spaces allowed
        </div>
      );
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationError('');

    if (!isLogin && !validatePassword()) {
      setLoading(false);
      return;
    }

    try {
      if (isResetPassword) {
        await resetPassword(email);
        alert('Password reset email sent! Check your inbox.');
        setIsResetPassword(false);
      } else {
        if (isLogin) {
          await login(email, password);
          onClose();
          navigate('/cheatsheet');
        } else {
          await signup(email, password);
          setIsVerificationSent(true);
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      if (error.message === 'Email not verified') {
        setIsVerificationSent(true);
      }
    }

    setLoading(false);
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      alert('Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('Failed to resend verification:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await loginWithGoogle();
      onClose();
      navigate('/cheatsheet');
    } catch (error) {
      console.error('Google sign-in error:', error);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setValidationError('');
    setPassword('');
    setConfirmPassword('');
  };

  if (!isOpen) return null;

  if (isVerificationSent) {
    return (
      <div className="auth-modal-overlay">
        <div className="auth-modal">
          <button className="close-button" onClick={onClose}>×</button>
          
          <h2>Verify Your Email</h2>
          
          <div className="verification-message">
            <p>We've sent a verification link to <strong>{email}</strong></p>
            <p>Please check your email and click the link to verify your account.</p>
            <p>After verification, you can log in to your account.</p>
          </div>
          
          <button className="link-button" onClick={handleResendVerification}>
            Didn't receive the email? Send again
          </button>
          
          <button 
            className="link-button" 
            onClick={() => {
              setIsVerificationSent(false);
              setIsLogin(true);
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal">
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>{isResetPassword ? 'Reset Password' : isLogin ? 'Login' : 'Sign Up'}</h2>
        
        {(error || validationError) && (
          <div className="error-message">
            {validationError || error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          {!isResetPassword && (
            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          )}

          {!isResetPassword && !isLogin && (
            <>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {getPasswordRequirements()}
            </>
          )}
          
          {!isResetPassword && isLogin && (
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMe">Remember me</label>
            </div>
          )}
          
          <button type="submit" disabled={loading}>
            {loading ? 'Loading...' : isResetPassword ? 'Send Reset Link' : isLogin ? 'LOGIN' : 'SIGN UP'}
          </button>
        </form>
        
        {!isResetPassword && (
          <>
            <div className="divider">or</div>
            
            <button className="google-button" onClick={handleGoogleSignIn}>
              Continue with Google
            </button>
          </>
        )}
        
        <div className="auth-links">
          {isResetPassword ? (
            <button className="link-button" onClick={() => setIsResetPassword(false)}>
              Back to login
            </button>
          ) : (
            <>
              {isLogin && (
                <button className="link-button" onClick={() => setIsResetPassword(true)}>
                  FORGOT PASSWORD?
                </button>
              )}
              <button className="link-button" onClick={switchMode}>
                {isLogin ? 'NEED AN ACCOUNT? SIGN UP' : 'ALREADY HAVE AN ACCOUNT? LOGIN'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 